'use client';

import { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';

export interface MultiSelectOption {
  id: number;
  label: string;
  icon?: React.ReactNode;
}

export const SearchableMultiSelect = ({
  label,
  options,
  selectedIds,
  onToggle,
  emptyLabel,
  searchPlaceholder,
}: {
  label: string;
  options: MultiSelectOption[];
  selectedIds: number[];
  onToggle: (id: number) => void;
  emptyLabel: string;
  searchPlaceholder: string;
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && menuRef.current) {
      const timer = setTimeout(() => {
        menuRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const filtered = options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="relative w-full">
      <label className="mb-1.5 block text-[0.8rem] font-medium tracking-[0.02em] text-white/60">{label}</label>

      <button
        type="button"
        className="flex w-full items-center justify-between rounded-[0.6rem] border border-white/10 bg-white/5 px-3.5 py-2.5 text-sm font-medium text-white/80 transition-all duration-200 hover:border-brand-teal/30 hover:bg-white/8 focus:border-brand-teal focus:shadow-[0_0_0_3px_rgba(70,192,186,0.12)] focus:outline-none cursor-pointer"
        onClick={() => setOpen(!open)}
      >
        <span>
          {selectedIds.length === 0
            ? emptyLabel
            : `${selectedIds.length} selecionado${selectedIds.length !== 1 ? 's' : ''}`}
        </span>
        <span
          className={`pointer-events-none text-sm text-white/40 transition-transform duration-200 ${open ? 'rotate-180 text-brand-teal' : ''}`}
        >
          ▾
        </span>
      </button>

      {open && (
        <div
          ref={menuRef}
          className="absolute inset-x-0 top-full z-100 mt-1.5 animate-dropdown-in overflow-hidden rounded-xl border border-brand-teal/25 bg-[rgba(10,16,32,0.96)] shadow-[0_10px_30px_rgba(0,0,0,0.5),0_0_30px_rgba(70,192,186,0.05)] backdrop-blur-lg"
        >
          <div className="border-b border-white/6 p-2.5">
            <input
              type="text"
              className="w-full rounded-[0.45rem] border border-white/8 bg-white/4 px-3 py-2 text-[0.82rem] text-white placeholder:text-white/25 transition-all duration-200 focus:border-brand-teal/40 focus:bg-brand-teal/3 focus:outline-none"
              placeholder={searchPlaceholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
          </div>
          <div className="flex max-h-[200px] flex-col gap-0.5 overflow-y-auto p-1.5">
            {filtered.length === 0 ? (
              <div className="p-3 text-center text-[0.8rem] text-white/35">{emptyLabel}</div>
            ) : (
              filtered.map((option) => {
                const checked = selectedIds.includes(option.id);
                return (
                  <button
                    key={option.id}
                    type="button"
                    className={`flex w-full items-center gap-2.5 rounded-[0.45rem] border px-2.5 py-2 text-left text-[0.82rem] font-medium transition-colors duration-150 cursor-pointer ${
                      checked
                        ? 'border-brand-teal/15 bg-brand-teal/8 text-white'
                        : 'border-transparent text-white/55 hover:bg-brand-teal/6 hover:text-white'
                    }`}
                    onClick={() => onToggle(option.id)}
                  >
                    {option.icon && <span className="flex shrink-0 scale-75 items-center text-brand-teal/70">{option.icon}</span>}
                    <span>{option.label}</span>
                    <div
                      className={`ml-auto flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                        checked ? 'border-brand-teal bg-brand-teal text-brand-bg' : 'border-white/20'
                      }`}
                    >
                      {checked && <Check size={10} />}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};
